import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useWizard } from "../../context/WizardContext";
import { US_STATE_CODES } from "../../types/wizard.types";
import type { ResidencyDetail } from "../../types/individual.types";
import { DateInput } from "../shared/DateInput";
import { Plus } from "lucide-react";

const currentYear = new Date().getFullYear();
const TAX_YEARS = [currentYear, currentYear - 1, currentYear - 2];

function getResidencies(list: ResidencyDetail[] | undefined, personType: "TAXPAYER" | "SPOUSE"): ResidencyDetail[] {
  if (!list) return [];
  return list.filter((r) => r.personType === personType);
}

export function IndividualStep2Residency() {
  const { individualData, updateIndividualData } = useWizard();
  const list = individualData.residencyDetails ?? [];
  const hasSpouse = (individualData.personalInformation ?? []).some((p) => p.personType === "SPOUSE");

  const taxpayerRes = getResidencies(list, "TAXPAYER");
  const spouseRes = getResidencies(list, "SPOUSE");

  const updateResidencies = (personType: "TAXPAYER" | "SPOUSE", items: ResidencyDetail[]) => {
    const other = list.filter((r) => r.personType !== personType);
    const next = [...other, ...items.map((r) => ({ ...r, personType }))];
    updateIndividualData({ residencyDetails: next });
  };

  const addState = (personType: "TAXPAYER" | "SPOUSE") => {
    const items = getResidencies(list, personType);
    updateResidencies(personType, [...items, { personType, year: currentYear }]);
  };

  const updateOne = (personType: "TAXPAYER" | "SPOUSE", index: number, patch: Partial<ResidencyDetail>) => {
    const items = [...getResidencies(list, personType)];
    if (!items[index]) return;
    items[index] = { ...items[index], ...patch };
    updateResidencies(personType, items);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Residency Details</h2>
        <p className="text-sm text-muted-foreground">
          At least one state of residence with state and dates is required for the current filing year.
        </p>
      </div>

      <Tabs defaultValue="taxpayer" className="w-full">
        <TabsList className="border border-primary/30 bg-primary/5">
          <TabsTrigger value="taxpayer" className="data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm data-[state=active]:border-black">
            Taxpayer
          </TabsTrigger>
          {hasSpouse && (
            <TabsTrigger value="spouse" className="data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm data-[state=active]:border-black">
              Spouse
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="taxpayer" className="space-y-4 mt-4">
          {taxpayerRes.length === 0 && (
            <p className="text-sm text-muted-foreground">Add at least one state (required).</p>
          )}
          {taxpayerRes.map((r, i) => (
            <Card key={i}>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">State {i + 1}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Tax Year</Label>
                  <Select
                    value={String(r.year ?? "")}
                    onValueChange={(v) => updateOne("TAXPAYER", i, { year: Number(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {TAX_YEARS.map((y) => (
                        <SelectItem key={y} value={String(y)}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Select
                    value={r.state ?? ""}
                    onValueChange={(v) => updateOne("TAXPAYER", i, { state: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="State" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATE_CODES.map((code) => (
                        <SelectItem key={code} value={code}>
                          {code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <DateInput
                    label="Period of stay (start)"
                    value={r.startDate}
                    onChange={(v) => updateOne("TAXPAYER", i, { startDate: v })}
                  />
                  <DateInput
                    label="Period of stay (end)"
                    value={r.endDate}
                    onChange={(v) => updateOne("TAXPAYER", i, { endDate: v })}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => addState("TAXPAYER")}>
            <Plus className="h-4 w-4 mr-2" />
            Add another state
          </Button>
        </TabsContent>
        {hasSpouse && (
          <TabsContent value="spouse" className="space-y-4 mt-4">
            {spouseRes.length === 0 && (
              <p className="text-sm text-muted-foreground">Add at least one state.</p>
            )}
            {spouseRes.map((r, i) => (
              <Card key={i}>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">State {i + 1}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Tax Year</Label>
                    <Select
                      value={String(r.year ?? "")}
                      onValueChange={(v) => updateOne("SPOUSE", i, { year: Number(v) })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Year" />
                      </SelectTrigger>
                      <SelectContent>
                        {TAX_YEARS.map((y) => (
                          <SelectItem key={y} value={String(y)}>
                            {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Select
                      value={r.state ?? ""}
                      onValueChange={(v) => updateOne("SPOUSE", i, { state: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="State" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATE_CODES.map((code) => (
                          <SelectItem key={code} value={code}>
                            {code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <DateInput
                      label="Period of stay (start)"
                      value={r.startDate}
                      onChange={(v) => updateOne("SPOUSE", i, { startDate: v })}
                    />
                    <DateInput
                      label="Period of stay (end)"
                      value={r.endDate}
                      onChange={(v) => updateOne("SPOUSE", i, { endDate: v })}
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => addState("SPOUSE")}>
              <Plus className="h-4 w-4 mr-2" />
              Add another state
            </Button>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
